'use client'

import Link from 'next/link'
import {
  type ComponentProps,
  type FocusEvent,
  type PointerEvent,
  useCallback,
  useState,
} from 'react'

/**
 * A `<Link>` for list rows that open a detail page (`/x/[id]`). By default it
 * prefetches the destination's App Shell like any link; once the person
 * shows intent (pointer over it, or keyboard focus) it upgrades to a
 * per-link prefetch, which resolves the row's `params` and the page's
 * prefetchable seeds — so the click paints the record, not its skeleton.
 *
 * Per-link prefetching on every visible row would cost one server render per
 * row; on intent it costs one per row the person reaches for (Next.js
 * "Optimizing prefetching": prefetch on intent for lists and grids).
 *
 * `isEager` starts the per-link prefetch as soon as the link is visible — for
 * the few most-likely rows (the top of a feed), whose detail prefetch takes
 * longer than a quick hover-and-click.
 */
export function IntentPrefetchLink({
  isEager = false,
  onPointerEnter,
  onFocus,
  ...props
}: Omit<ComponentProps<typeof Link>, 'prefetch'> & { isEager?: boolean }) {
  const [hasIntent, setHasIntent] = useState(false)
  const handlePointerEnter = useCallback(
    (event: PointerEvent<HTMLAnchorElement>) => {
      setHasIntent(true)
      onPointerEnter?.(event)
    },
    [onPointerEnter]
  )
  const handleFocus = useCallback(
    (event: FocusEvent<HTMLAnchorElement>) => {
      setHasIntent(true)
      onFocus?.(event)
    },
    [onFocus]
  )
  return (
    <Link
      {...props}
      prefetch={isEager || hasIntent ? true : null}
      onPointerEnter={handlePointerEnter}
      onFocus={handleFocus}
    />
  )
}
